import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { PopupType } from "src/app/shared/enums/popup-types";
import { showMessage } from "src/app/shared/utils/toast.popup";
import { EInvoiceService } from "src/app/shared/services/e-invoice.service";
import { ReverseChargeReason } from "src/app/shared/types/e-invoice";
import { environment } from "src/environments/environment";
import { TaxCategoryOption, TaxLabels, TaxRateCategory, TaxRateCategoryToAdd } from "src/app/shared/types/tax-rate-categories";
import { TaxRateCategoriesService } from "src/app/shared/services/tax-rate-categories.service";

@Component({
  selector: "app-tax-rates-modal",
  templateUrl: "./tax-rates.modal.component.html",
  standalone: false,
})
export class TaxRatesModalComponent implements OnInit, OnDestroy {
  @Input() category!: TaxRateCategory;
  @Input() edit!: boolean;
  @ViewChild("nameInput") nameInput!: ElementRef;
  @Output() updated: EventEmitter<null> = new EventEmitter();
  editMode = false;

  public test = false;
  public form!: FormGroup;
  public isLoading = false;

  private categorySubscription: Subscription | undefined;
  taxCategoriesOptions!: TaxLabels[];
  reverseChargeReasons: ReverseChargeReason[] = [];
  filteredReasons: ReverseChargeReason[] = [];
  selectedCategory!: string;
  selectedKey = "";
  decisionNumber = "";
  showTaxForm = false;

  taxCategoriesOptionsProd = [
    { value: 0, label: "А - 0%", category: "VAT", taxLabel: "А" },
    { value: 0, label: "Г - 00%", category: "VAT", taxLabel: "Г" },
    { value: 20, label: "Ђ - 20%", category: "VAT", taxLabel: "Ђ" },
    { value: 10, label: "Е - 10%", category: "VAT", taxLabel: "Е" },
    { value: 10, label: "S - 10%", category: "VAT", taxLabel: "S" },
    { value: 20, label: "S - 20%", category: "VAT", taxLabel: "S" },
    { value: 0, label: "AE - 20", category: "VAT", taxLabel: "AE20" },
    { value: 0, label: "AE - 10", category: "VAT", taxLabel: "AE10" },
    { value: 0, label: "Z - 0%", category: "VAT", taxLabel: "Z" },
    { value: 0, label: "E - 0%", category: "VAT", taxLabel: "E" },
    { value: 0, label: "R - 0%", category: "VAT", taxLabel: "R" },
    { value: 0, label: "O - 0%", category: "VAT", taxLabel: "O" },
    { value: 0, label: "OE - 0%", category: "VAT", taxLabel: "OE" },
    { value: 0, label: "SS - 0%", category: "VAT", taxLabel: "SS" },
    { value: 0, label: "N - -1%", category: "VAT", taxLabel: "N" },
  ];

  taxCategoriesOptionsDev = [
    { value: 10, label: "A - 10%", category: "VAT", taxLabel: "A" },
    { value: 0, label: "B - 0%", category: "VAT", taxLabel: "B" },
    { value: 19, label: "Ж - 19%", category: "VAT", taxLabel: "Ж" },
    { value: 10, label: "S - 10%", category: "VAT", taxLabel: "S" },
    { value: 20, label: "S - 20%", category: "VAT", taxLabel: "S" },
    { value: 0, label: "AE - 20", category: "VAT", taxLabel: "AE20" },
    { value: 0, label: "AE - 10", category: "VAT", taxLabel: "AE10" },
    { value: 0, label: "Z - 0%", category: "VAT", taxLabel: "Z" },
    { value: 0, label: "E - 0%", category: "VAT", taxLabel: "E" },
    { value: 0, label: "R - 0%", category: "VAT", taxLabel: "R" },
    { value: 0, label: "O - 0%", category: "VAT", taxLabel: "O" },
    { value: 0, label: "OE - 0%", category: "VAT", taxLabel: "OE" },
    { value: 0, label: "SS - 0%", category: "VAT", taxLabel: "SS" },
    { value: 0, label: "N - -1%", category: "VAT", taxLabel: "N" },
  ];

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private taxRateService: TaxRateCategoriesService,
    public eInvoiceService: EInvoiceService,
  ) {}

  ngOnInit(): void {
    this.taxCategoriesOptions = environment.production ? this.taxCategoriesOptionsProd : this.taxCategoriesOptionsDev;
    this.initForm(this.category);
    this.getReverseChargeReasons();
  }

  initForm(taxRateCategory: TaxRateCategory): void {
    const { title, code, taxType, taxLabel, percentage, desicionNumber, description } = taxRateCategory || ({} as TaxRateCategory);

    this.form = this.fb.group({
      title: [title, Validators.required],
      category: [null, Validators.required],
      percentage: [percentage, Validators.required],
      type: [taxType, Validators.required],
      code: [code],
      desicionNumber: [desicionNumber],
      description: [description],
    });

    this.categorySubscription = this.form.get("category")?.valueChanges.subscribe(category => this.onCategoryChange(category));
    if (taxLabel) {
      const defaultCategory = this.taxCategoriesOptions.find(option => option.taxLabel === taxLabel && option.value === percentage);
      this.form.get("category")?.setValue(defaultCategory);
      setTimeout(() => {
        if (defaultCategory) {
          this.setReasonsByCategory(defaultCategory);
        }
      }, 1000);

      if (code) {
        this.form.get("code")?.setValue(code);
      }
    }
  }

  onCategoryChange(category: TaxCategoryOption): void {
    if (category) {
      this.form.get("percentage")?.setValue(category.value);
      this.form.get("type")?.setValue(category.category);
    }
  }

  getReverseChargeReasons(): void {
    this.isLoading = true;
    const subscription = this.eInvoiceService.getReverseChargeReasons().subscribe({
      next: (reasons: ReverseChargeReason[]) => {
        subscription.unsubscribe();
        this.reverseChargeReasons = reasons;
        this.filteredReasons = [];
        this.isLoading = false;
      },
      error: () => {
        subscription.unsubscribe();
        this.isLoading = false;
      },
    });
  }

  setReasonsByCategory(selectedCategory: TaxCategoryOption): void {
    this.filteredReasons = [];
    if (selectedCategory?.taxLabel) {
      this.selectedCategory = String(selectedCategory.taxLabel).trim();
      this.filteredReasons = this.reverseChargeReasons.filter(reason => reason.category === this.selectedCategory);
    }
  }

  submitForm(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.edit) {
      this.isLoading = true;
      const addSubscription = this.taxRateService.createTaxRateCategory(this.getCategoryData()).subscribe({
        next: () => {
          this.isLoading = false;
          addSubscription.unsubscribe();
          this.updated.emit();
          showMessage(PopupType.Success, this.translate.instant("tax_categories.created_successfully"));
        },
        error: (error) => {
          this.isLoading = false;
          addSubscription.unsubscribe();
          showMessage(PopupType.Danger, error?.error?.toString() || error?.message || "Error creating tax category", error);
        },
      });
    }
    else {
      this.isLoading = true;
      const editSubscription = this.taxRateService.updateTaxRateCategory(this.category.id, this.getCategoryData()).subscribe({
        next: () => {
          this.isLoading = false;
          editSubscription.unsubscribe();
          this.updated.emit();
          showMessage(PopupType.Success, this.translate.instant("tax_categories.edited_successfully"));
        },
        error: (error) => {
          this.isLoading = false;
          editSubscription.unsubscribe();
          showMessage(PopupType.Danger, error?.error?.toString() || error?.message || "Error updating tax category", error);
        },
      });
    }
  }

  getCategoryData(): TaxRateCategoryToAdd {
    const { title, category, type, code, percentage, desicionNumber, description } = this.form.getRawValue();
    return {
      title,
      code,
      percentage,
      desicionNumber,
      description,
      taxLabel: category?.taxLabel || "",
      taxType: type,
    } as TaxRateCategoryToAdd;
  }

  ngOnDestroy(): void {
    if (this.categorySubscription) {
      this.categorySubscription.unsubscribe();
    }
  }
}
